import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { makeStyles } from '@material-ui/core/styles';
import InputAdornment from '@material-ui/core/InputAdornment';
import Checkbox from '@material-ui/core/Checkbox';
import Collapse from '@material-ui/core/Collapse';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Grid from '@material-ui/core/Grid';
import HelpIcon from '@material-ui/icons/Help';
import IconButton from '@material-ui/core/IconButton';
import LabelIcon from '@material-ui/icons/Label';
import MemoryIcon from '@material-ui/icons/Memory';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import Zoom from '@material-ui/core/Zoom';

const useStyles = makeStyles((theme) => ({
  root: {
    '& .MuiTextField-root': {
      margin: theme.spacing(1),
      width: "100%",
    },
  },
  grid: {
  },  
  expand: {
    transform: 'rotate(0deg)',
    // marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
  confirm: {
    display: 'flex',
    alignItems: 'center',
    margin: '6px',
  },
  inputSpec: {
    marginRight: theme.spacing(1),
  }
}));

export default function ConfirmExecutionStep(props) {
  
  const { 
    request,
    defaults,
    onDescriptionChanged,
    onImageChanged,
    onMemoryChanged,
    onTargetFolderChanged,
    onLimitChanged, 
    onDryrunChanged, 
  } = props;

  const [expanded, setExpanded] = React.useState(false);
  const [isLimit, setIsLimit] = React.useState(false);

  const [targetFolderError, setTargetFolderError] = React.useState(null);
  const [limitError, setLimitError] = React.useState(null);

  const classes = useStyles();

  const handleDescriptionChanged = (event) => {
    if(onDescriptionChanged) {
      onDescriptionChanged(event.target.value);
    }
  };
  const handleImageChanged = (event) => {
    if(onImageChanged) {
      onImageChanged(event.target.value);
    }
  };
  const handleMemoryChanged = (event) => {
    if(onMemoryChanged) {
      onMemoryChanged(event.target.value);
    }
  };
  const handleTargetFolderChanged = (event) => {
    let newTargetFolder=event.target.value;
    if(['staged','workload'].includes(newTargetFolder)) {
      setTargetFolderError(`'${newTargetFolder}' is a reserved name`);
      return;
    } else {
      setTargetFolderError(null);
    }
    if(newTargetFolder.match(/^[0-9a-z]+$/) || newTargetFolder==='') {
      if(onTargetFolderChanged) {
        onTargetFolderChanged(event.target.value);
      }
    }
  };
  const handleLimitEnabled = (event) => {
    if(event.target.checked) {
      setIsLimit(true);
      onLimitChanged(1);
    } else {
      setIsLimit(false);
      onLimitChanged(0);
    }
  };
  const handleLimitChanged = (event) => {
    let newLimit=parseInt(event.target.value, 10);
    if(onLimitChanged) {
      if(newLimit<=1000) {
        onLimitChanged(newLimit);
        setLimitError(null);
      } else if(isNaN(newLimit)) {
        onLimitChanged(0);
        setLimitError(null);
      } else {
        setLimitError('max 1000');
      }
    }
  };
  const handleDryrunChanged = (event) => {
    if(onDryrunChanged) {
      onDryrunChanged(event.target.checked);
    }
  };

  const handleExpandClick = () => {
    setExpanded(prev => !prev);
  };

  const previewSelector = JSON.stringify(request,null,2);

  return (
    <form className={classes.root} noValidate autoComplete="off">
      <Grid className={classes.grid} container spacing={1}>
        <Grid item xs={12}>
          <Typography color="textSecondary">
            Adjust defaults as needed and start your execution... 
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <TextField
            id="execDescription"
            label="Execution Comment"
            placeholder="e.g. Backfill 2018"
            defaultValue={request.Description || ''}
            onChange={handleDescriptionChanged}
            variant="outlined" size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            id="overrideImage"
            label="Image Tag"
            placeholder={defaults.image}
            defaultValue={request.Overrides.Image || defaults.image }
            onChange={handleImageChanged}
            variant="outlined" size="small"
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><LabelIcon color="disabled"/></InputAdornment>,
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            id="targetFolder"
            label="Target Folder"
            placeholder={defaults.targetFolder}
            defaultValue={request.Overrides.TargetFolder || defaults.targetFolder}
            onChange={handleTargetFolderChanged}
            helperText={targetFolderError}
            variant="outlined" size="small"
            error={targetFolderError!==null}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">/</InputAdornment>,
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            select
            id="overrideMemory"
            label="Memory"
            placeholder={defaults.memory}
            value={request.Overrides.Memory || defaults.memory}
            onChange={handleMemoryChanged}
            variant="outlined" size="small"
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><MemoryIcon color="disabled"/></InputAdornment>,
              //endAdornment: <InputAdornment position="end">MB</InputAdornment>,
            }}
          >
            <MenuItem value=""></MenuItem>
            <MenuItem value="512">512</MenuItem>
            <MenuItem value="1024">1024</MenuItem>
            <MenuItem value="2048">2048</MenuItem>
            <MenuItem value="3072">3072</MenuItem>
            <MenuItem value="4096">4096</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={6}>
          <div style={{ display: 'flex', alignItems: 'center'}}>
            <Checkbox
              checked={isLimit}
              onChange={handleLimitEnabled}
              name="limit"
              color="primary"
            />
            <Typography>Input Limit:</Typography>
            <TextField style={{ maxWidth: '75px' }}
              id="limitValue"
              value={request.Inputs.Limit || ''}
              onChange={handleLimitChanged}
              size="small"
              inputProps={{ pattern: '[0-9]*' }}
              disabled={!isLimit}
              error={limitError!==null}
              helperText={limitError}
            />
          </div>
        </Grid>
        <Grid item xs={6}>
          <div style={{ display: 'flex', alignItems: 'center'}}>
            <Checkbox
              checked={request.Overrides.Dryrun || false}
              onChange={handleDryrunChanged}
              name="dryrun"
              color="primary"
            />
            <Typography>
              Dry Run
            </Typography>
            <Tooltip TransitionComponent={Zoom} title="Your workload will process inputs, but no outputs will be written.  The target dataset will not be modified.  Use for testing changes to your workload.">
              <HelpIcon color="action" style={{ maxWidth: '16px', leftMargin: '8px'}}/>
            </Tooltip>
          </div>
        </Grid>
        <Grid item xs={12}>
          <div className={classes.confirm}>
            <IconButton className={clsx(classes.expand, {
               [classes.expandOpen]: expanded, })}
              onClick={handleExpandClick}
              aria-expanded={expanded}
              aria-label="show more"
              size="small"
            >
              <ExpandMoreIcon />
            </IconButton>        
            <Typography style={{ marginLeft: '5px'}}>Confirm and execute</Typography>
          </div>
          <div className={classes.inputSpec} >
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <TextField
                id="input-selector"
                label="Input Specfication"
                variant="outlined" size="small"
                multiline
                rows={4}
                value={previewSelector}
              />
            </Collapse>
          </div>
        </Grid>
      </Grid>
    </form>
  );
}

ConfirmExecutionStep.propTypes = {
  defaults: PropTypes.object.isRequired,
  request: PropTypes.object.isRequired,
  onDescriptionChanged: PropTypes.func,
  onImageChanged: PropTypes.func,
  onMemoryChanged: PropTypes.func,
  onTargetFolderChanged: PropTypes.func,
  onLimitChanged: PropTypes.func,
  onDryrunChanged: PropTypes.func,
};
